// ----------------------------------------------------------------------
// <copyright file="Izenda.VirtualListUIStore.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
!(function() {
  var action = require('mobx').action;
  var extendObservable = require('mobx').extendObservable;
  var { get, flatMap, placeholder } = require('lodash/fp');

  function VirtualListUIStore(pageToScreenRatio, items) {
    this.pageToScreenRatio = pageToScreenRatio;
    this.numPageOnScreen = 3;
    this.training = true;

    extendObservable(this, {
      width: 0,
      startIndex: 0,
      viewportHeight: 0,
      viewportWidth: 0,
      itemHeight: 0,
      itemWidth: 0,
      pages: [],
      items: function() {
        return flatMap(function(page) {
          return page.items.slice();
        }, this.pages.slice());
      },
      virtualHeight: function() {
        return placeholder(this.pages)
          .map(get('height'))
          .reduce(function(acc, height) {
            return acc + height;
          }, 0);
      },
      screenPageHeight: function() {
        return this.viewportHeight ? this.viewportHeight * this.pageToScreenRatio : Infinity;
      },
      bufferHeight: function() {
        var startIndex = this.startIndex;

        return placeholder(this.pages)
          .filter(function(page) {
            return page.id < startIndex;
          })
          .map(get('height'))
          .reduce(function(acc, height) {
            return acc + height;
          }, 0);
      },
      virtualItemsOnScreen: function() {
        var startIndex = this.startIndex;
        var lastIndex = Math.min(this.pages.length, startIndex + this.numPageOnScreen) - 1;
        return placeholder(this.pages)
          .filter(function(page) {
            return page.id >= startIndex && page.id <= lastIndex;
          })
          .flatMap(get('items'))
          .value();
      }
    });

    this.initialize(items, 0);
  }

  VirtualListUIStore.prototype.initialize = action(function initialize(items, getChildItems) {
    if (!items || items.length === 0) return;

    var lastPage, itemIndex, itemsLength, item;
    var childIndex, childLength, childItem, childItems;

    if (this.pages.length > 0) {
      lastPage = this.pages[this.startIndex];
    } else {
      lastPage = this.newVirtualPage();
    }

    if (this.training) {
      // Render only one item for training its height
      lastPage.append(new VirtualItem(items[0], true));
      this.training = false;
    } else {
      // Take one item dimension as default item dimension
      if (!this.itemHeight) this.itemHeight = lastPage.items[0].height;
      if (!this.itemWidth) this.itemWidth = lastPage.items[0].width;

      // Clear out training rendered items/pages.
      this.release();

      // Intentionally use for loop that give best performance on large item list.
      for (itemIndex = 0, itemsLength = items.length; itemIndex < itemsLength; itemIndex++) {
        item = items[itemIndex];
        this.append(new VirtualItem(item));

        if (getChildItems) {
          childItems = getChildItems(item);

          // Scan thru child items and append virtual item for child items
          for (childIndex = 0, childLength = childItems.length; childIndex < childLength; childIndex++) {
            childItem = childItems[childIndex];
            this.append(new VirtualItem(childItem));
          }
        }
      }
    }
  });

  VirtualListUIStore.prototype.append = action(function appendItem(item) {
    if (!item) return;
    var pages = this.pages;
    var lastPage = pages.length > 0 ? pages[pages.length - 1] : null;

    if (!lastPage || !lastPage.hasVacancy) {
      lastPage = this.newVirtualPage();
    }
    lastPage.append(item);
  });

  VirtualListUIStore.prototype.release = action(function release() {
    this.pages = [];
    VirtualItemRegistry.reset();
  });

  VirtualListUIStore.prototype.setViewportDimension = action(function setViewportDimension(width, height) {
    this.viewportWidth = width;
    this.viewportHeight = height;
    this.repartition();
  });

  VirtualListUIStore.prototype.calculateStartIndex = function(scrollTop) {
    var runningTop = 0;
    var current = placeholder(this.pages)
      .map(function(page) {
        var pageCoord = {
          id: page.id,
          top: runningTop,
          bottom: runningTop + page.height
        };
        runningTop += page.height;
        return pageCoord;
      })
      .findLast(function(pageCoord) {
        return scrollTop >= pageCoord.top && scrollTop <= pageCoord.bottom;
      });

    // The top page index that is showing in viewport
    var topPageIndex = current ? current.id : 0;
    // Max start index
    var maxStartIndex = Math.max(this.pages.length - this.numPageOnScreen, 0);
    // Shift the start page index lower than showing page index
    var startIndex = Math.min(Math.max(topPageIndex - 1, 0), maxStartIndex);

    if (startIndex !== this.startIndex) {
      this.startIndex = startIndex;
    }
  };

  VirtualListUIStore.prototype.repartition = action(function repartition() {
    var index, length, itemIndex, pageLength, currPage, currItems, currItem;
    var pages = this.pages.peek();

    this.release();

    var newPage = this.newVirtualPage();

    for (index = 0, length = pages.length; index < length; index++) {
      currPage = pages[index];
      currItems = currPage.items;

      for (itemIndex = 0, pageLength = currItems.length; itemIndex < pageLength; itemIndex++) {
        currItem = currItems[itemIndex];
        if (newPage.hasVacancy) {
          newPage.append(currItem);
        } else {
          newPage = this.newVirtualPage();
          newPage.append(currItem);
        }
      }
    }
  });

  VirtualListUIStore.prototype.newVirtualPage = function() {
    var newPage = new VirtualPage(this);
    this.pages.push(newPage);
    return newPage;
  };

  function VirtualPage(parent) {
    this.id = parent.pages.length;
    this.parent = parent;
    this.items = [];

    extendObservable(this, {
      lazyLoaded: false,
      onScreen: false,
      // height : 0,
      top: function() {
        return 0;
      },
      bottom: function() {
        return this.top + this.height;
      },
      width: function() {
        return placeholder(this.items)
          .map(get('width'))
          .reduce(function(acc, width) {
            return acc + (width === 0 ? parent.itemWidth : width);
          }, 0);
      },
      height: function() {
        // return this.items.length * this.parent.itemHeight;
        return placeholder(this.items)
          .map(get('height'))
          .reduce(function(acc, height) {
            return acc + (height === 0 ? parent.itemHeight : height);
          }, 0);
      },
      hasVacancy: function() {
        return this.height < this.parent.screenPageHeight;
      }
    });
  }

  VirtualPage.prototype.append = action(function appendItem(item) {
    this.items.push(item);
  });

  function VirtualItem(dataItem, isTrainingMode) {
    this.itemIndex = VirtualItemRegistry.generateItemIndex();
    this.dataItem = dataItem;
    this.isTrainingMode = isTrainingMode;

    extendObservable(this, {
      top: 0,
      bottom: 0,
      width: 0,
      height: 0
    });
  }

  VirtualItem.prototype.updateCoords = action(function updateCoords(domElm) {
    if (domElm) {
      this.height = domElm.offsetHeight;
      this.width = domElm.offsetWidth;
    }
  });

  var VirtualItemRegistry = (function() {
    var itemCount = 0;
    return {
      generateItemIndex: function() {
        return itemCount++;
      },
      reset: function() {
        itemCount = 0;
      }
    };
  })();

  module.exports = VirtualListUIStore;
})();



// WEBPACK FOOTER //
// ./ui/share/components/controls/Izenda.VirtualListUIStore.js